import { ARCHES, CANINE, MOLAR } from 'constants/prescriptionConstants';
import {
  ANTERIOR_BITE_RAMPS,
  CHIEF_COMPLAINTS,
  CROWDING,
  DENTITION_TYPE,
  DENTITION_TYPE_OPTIONS,
  MAIN_OBJECTIVES,
  OVERBITE_CORRECT_OPEN_BITE,
  OVERBITE_IMPROVE_DEEP_BITE_LOWER,
  OVERBITE_IMPROVE_DEEP_BITE_UPPER,
  PATIENT_DIAGNOSIS_OPTIONS,
  RESOLVE_CROWDING_OPTION_LOWER,
  RESOLVE_CROWDING_OPTION_UPPER,
  SAGITTAL_ACTIONS_OPTIONS
} from 'constants/prescriptionOptions';
import { UltraTreatmentSpecType } from 'types/UltraPrescription.types';

import { getSagittalTreatmentOptions } from './ultraPrescription.sagittal.utils';
import {
  convertToOptionsType,
  getStringValue,
  getSubOptionFromList
} from './ultraPrescription.shared';
import {
  formatTeethsForController,
  getToothChartValues,
  splitTeethByDentition,
  splitTeethOrTextField
} from './ultraPrescription.teeth.utils';

// Resolves ultra-prefixed API keys with fallback to legacy unprefixed Ultra keys.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeUltraTpv = (tpv?: Record<string, any>) => ({
  anglesClassification:
    tpv?.ultraAnglesClassification ?? tpv?.anglesClassification,
  patientDiagnosis: tpv?.ultraPatientDiagnosis ?? tpv?.patientDiagnosis,
  patientDiagnosisText:
    tpv?.ultraPatientDiagnosisText ?? tpv?.patientDiagnosisText,
  chiefComplaints: tpv?.ultraChiefComplaints ?? tpv?.chiefComplaints,
  chiefComplaintsText:
    tpv?.ultraChiefComplaintsText ?? tpv?.chiefComplaintsText,
  mainObjectives: tpv?.ultraMainObjectives ?? tpv?.mainObjectives,
  mainObjectivesText: tpv?.ultraMainObjectivesText ?? tpv?.mainObjectivesText,
  archesToTreat: tpv?.ultraArchesToTreat ?? tpv?.archesToTreat,
  needPassiveAligners: tpv?.needPassiveAligners,
  toothInfo: tpv?.ultraToothInfo ?? tpv?.toothInfo,
  toothMovementRestrictions:
    tpv?.ultraToothMovementRestrictions ?? tpv?.toothMovementRestrictions,
  toothUnavailableForAttachment:
    tpv?.ultraToothUnavailableForAttachment ??
    tpv?.toothUnavailableForAttachment,
  sagittalRelationship:
    tpv?.ultraSagittalRelationship ?? tpv?.sagittalRelationship,
  overjet: tpv?.ultraOverjet ?? tpv?.overjet,
  overbite: tpv?.ultraOverbite ?? tpv?.overbite,
  midlines: tpv?.ultraMidlines ?? tpv?.midlines,
  ultraMidlinesShifts: tpv?.ultraMidlinesShifts,
  spacing: tpv?.ultraSpacing ?? tpv?.spacing,
  crowding: tpv?.ultraCrowding ?? tpv?.crowding,
  crowdingExtraction: tpv?.ultraTeethToExtract ?? tpv?.crowdingExtraction,
  teethToExtract: tpv?.teethToExtract,
  specialNotes: tpv?.specialNotes,
  translatedSpecialNotes: tpv?.translatedSpecialNotes,
  isAuxiliariesPresent: tpv?.isAuxiliariesPresent,
  auxiliaryButtons: tpv?.auxiliaryButtons,
  auxiliaryPrecisionCuts: tpv?.auxiliaryPrecisionCuts,
  isAdditionalApplianceRequired: tpv?.isAdditionalApplianceRequired,
  tads: tpv?.tads,
  sectionalBraces: tpv?.sectionalBraces,
  otherAppliances: tpv?.otherAppliances
});

export const formatUltraForForm = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tpv?: Record<string, any>
): UltraTreatmentSpecType => {
  const normalized = normalizeUltraTpv(tpv);
  const lowerResolveCrowding = getSubOptionFromList(
    'Lower',
    normalized?.crowding?.options
  );
  const upperResolveCrowding = getSubOptionFromList(
    'Upper',
    normalized?.crowding?.options
  );
  const upperMidlineShift = normalized?.ultraMidlinesShifts?.[0] || undefined;
  const lowerMidlineShift = normalized?.ultraMidlinesShifts?.[1] || undefined;
  const toothInfoMissingTeeth = normalized?.toothInfo?.missingTeeth;
  const {
    adultTeeth: toothInfoAdultTeeth,
    primaryTeeth: legacyPrimaryInMissingTeeth
  } = splitTeethByDentition(toothInfoMissingTeeth);
  const toothInfoPrimaryField = splitTeethOrTextField(
    normalized?.toothInfo?.primaryDentition
  );
  const toothInfoPrimaryTeeth =
    toothInfoPrimaryField.teeth || legacyPrimaryInMissingTeeth;
  const movementPrimaryField = splitTeethOrTextField(
    normalized?.toothMovementRestrictions?.primaryDentition
  );
  const movementAdultTeeth =
    normalized?.toothMovementRestrictions?.restrictions;
  const movementPrimaryTeeth = movementPrimaryField.teeth;
  const toothInfoDentitionValues = [
    ...(toothInfoAdultTeeth || !toothInfoMissingTeeth
      ? [DENTITION_TYPE.ADULT_DENTITION]
      : []),
    ...(toothInfoPrimaryTeeth ? [DENTITION_TYPE.PRIMARY_DENTITION] : [])
  ];
  const movementDentitionValues = [
    ...(movementAdultTeeth ? [DENTITION_TYPE.ADULT_DENTITION] : []),
    ...(movementPrimaryTeeth ? [DENTITION_TYPE.PRIMARY_DENTITION] : []),
    ...(!movementAdultTeeth && !movementPrimaryTeeth
      ? [DENTITION_TYPE.ADULT_DENTITION]
      : [])
  ];
  const attachmentPrimaryField = splitTeethOrTextField(
    normalized?.toothUnavailableForAttachment?.primaryDentition
  );
  const attachmentAdultTeeth =
    normalized?.toothUnavailableForAttachment?.noAttachmentValues;
  const attachmentPrimaryTeeth = attachmentPrimaryField.teeth;
  const attachmentDentitionValues = [
    ...(attachmentAdultTeeth ? [DENTITION_TYPE.ADULT_DENTITION] : []),
    ...(attachmentPrimaryTeeth ? [DENTITION_TYPE.PRIMARY_DENTITION] : []),
    ...(!attachmentAdultTeeth && !attachmentPrimaryTeeth
      ? [DENTITION_TYPE.ADULT_DENTITION]
      : [])
  ];
  const extractionPrimaryField = splitTeethOrTextField(
    normalized?.crowdingExtraction?.primaryDentition
  );
  const extractionAdultTeeth = normalized?.teethToExtract;
  const extractionPrimaryTeeth = extractionPrimaryField.teeth;
  const extractionDentitionValues = [
    ...(extractionAdultTeeth ? [DENTITION_TYPE.ADULT_DENTITION] : []),
    ...(extractionPrimaryTeeth ? [DENTITION_TYPE.PRIMARY_DENTITION] : []),
    ...(!extractionAdultTeeth && !extractionPrimaryTeeth
      ? [DENTITION_TYPE.ADULT_DENTITION]
      : [])
  ];
  return {
    angleClassification: normalized?.anglesClassification || '',
    patientDiagnosis: {
      diagnosis: convertToOptionsType(
        PATIENT_DIAGNOSIS_OPTIONS,
        normalized?.patientDiagnosis
      ),
      otherText: normalized?.patientDiagnosisText || ''
    },
    chiefComplaints: {
      complaints: convertToOptionsType(
        CHIEF_COMPLAINTS,
        normalized?.chiefComplaints
      ),
      otherText: normalized?.chiefComplaintsText || ''
    },
    mainObjectives: {
      objectives: convertToOptionsType(
        MAIN_OBJECTIVES,
        normalized?.mainObjectives
      ),
      otherText: normalized?.mainObjectivesText || ''
    },
    archesToTreat: {
      arches: normalized?.archesToTreat || '',
      passiveAligners: normalized?.needPassiveAligners || ''
    },
    toothInformation: {
      information: normalized?.toothInfo?.value || '',
      dentitionSelection: convertToOptionsType(
        DENTITION_TYPE_OPTIONS,
        toothInfoDentitionValues
      ),
      toothChart: formatTeethsForController(toothInfoAdultTeeth),
      primaryToothChart: formatTeethsForController(toothInfoPrimaryTeeth)
    },
    toothMovementRestriction: {
      restriction: normalized?.toothMovementRestrictions?.value || '',
      dentitionSelection: convertToOptionsType(
        DENTITION_TYPE_OPTIONS,
        movementDentitionValues
      ),
      toothChart: formatTeethsForController(movementAdultTeeth),
      primaryToothChart: formatTeethsForController(movementPrimaryTeeth)
    },
    toothAttachment: {
      attachment: normalized?.toothUnavailableForAttachment?.value || '',
      dentitionSelection: convertToOptionsType(
        DENTITION_TYPE_OPTIONS,
        attachmentDentitionValues
      ),
      toothChart: formatTeethsForController(attachmentAdultTeeth),
      primaryToothChart: formatTeethsForController(attachmentPrimaryTeeth)
    },
    sagittalRelationship: {
      relationship: normalized?.sagittalRelationship?.value || '',
      bothRandL: {
        rightAndLeftAction: convertToOptionsType(
          SAGITTAL_ACTIONS_OPTIONS,
          normalized?.sagittalRelationship?.optionsRowOne?.value
        ),
        afterAlignment: {
          text:
            getStringValue(
              normalized,
              'sagittalRelationship.optionsRowOne.showResultingAfterAlignment.value'
            ) || ''
        },
        improveCanine: {
          canine:
            normalized?.sagittalRelationship?.optionsRowOne
              ?.improveCanineRelationshipOptions?.value || '',
          ...getSagittalTreatmentOptions(
            CANINE,
            normalized,
            'sagittalRelationship.optionsRowOne.improveCanineRelationshipOptions.treatmentOptions'
          )
        },
        improveMolar: {
          molar:
            normalized?.sagittalRelationship?.optionsRowOne
              ?.improveMolarRelationship?.value || '',
          ...getSagittalTreatmentOptions(
            MOLAR,
            normalized,
            'sagittalRelationship.optionsRowOne.improveMolarRelationship.treatmentOptions'
          )
        }
      },
      eitherRorL: {
        right: {
          rightAction: convertToOptionsType(
            SAGITTAL_ACTIONS_OPTIONS,
            normalized?.sagittalRelationship?.optionsRowOne?.value
          ),
          afterAlignment: {
            text:
              getStringValue(
                normalized,
                'sagittalRelationship.optionsRowOne.showResultingAfterAlignment.value'
              ) || ''
          },
          improveCanine: {
            canine:
              normalized?.sagittalRelationship?.optionsRowOne
                ?.improveCanineRelationshipOptions?.value || '',
            ...getSagittalTreatmentOptions(
              CANINE,
              normalized,
              'sagittalRelationship.optionsRowOne.improveCanineRelationshipOptions.treatmentOptions'
            )
          },
          improveMolar: {
            molar:
              normalized?.sagittalRelationship?.optionsRowOne
                ?.improveMolarRelationship?.value || '',
            ...getSagittalTreatmentOptions(
              MOLAR,
              normalized,
              'sagittalRelationship.optionsRowOne.improveMolarRelationship.treatmentOptions'
            )
          }
        },
        left: {
          leftAction: convertToOptionsType(
            SAGITTAL_ACTIONS_OPTIONS,
            normalized?.sagittalRelationship?.optionsRowTwo?.value
          ),
          afterAlignment: {
            text:
              getStringValue(
                normalized,
                'sagittalRelationship.optionsRowTwo.showResultingAfterAlignment.value'
              ) || ''
          },
          improveCanine: {
            canine:
              normalized?.sagittalRelationship?.optionsRowTwo
                ?.improveCanineRelationshipOptions?.value || '',
            ...getSagittalTreatmentOptions(
              CANINE,
              normalized,
              'sagittalRelationship.optionsRowTwo.improveCanineRelationshipOptions.treatmentOptions'
            )
          },
          improveMolar: {
            molar:
              normalized?.sagittalRelationship?.optionsRowTwo
                ?.improveMolarRelationship?.value || '',
            ...getSagittalTreatmentOptions(
              MOLAR,
              normalized,
              'sagittalRelationship.optionsRowTwo.improveMolarRelationship.treatmentOptions'
            )
          }
        }
      }
    },
    overjet: normalized?.overjet || '',
    overbite: {
      overBite: normalized?.overbite?.value?.[0] || '',
      correctOpenBite: {
        Upper: {
          upper: convertToOptionsType(
            OVERBITE_CORRECT_OPEN_BITE,
            getSubOptionFromList('Upper', normalized?.overbite?.correctOpenBite)
              ?.options?.value
          ),
          upperOtherText:
            getSubOptionFromList('Upper', normalized?.overbite?.correctOpenBite)
              ?.options?.text || ''
        },
        Lower: {
          lower: convertToOptionsType(
            OVERBITE_CORRECT_OPEN_BITE,
            getSubOptionFromList('Lower', normalized?.overbite?.correctOpenBite)
              ?.options?.value
          ),
          lowerOtherText:
            getSubOptionFromList('Lower', normalized?.overbite?.correctOpenBite)
              ?.options?.text || ''
        }
      },
      ImproveDeepBite: {
        Upper: {
          upper: convertToOptionsType(
            OVERBITE_IMPROVE_DEEP_BITE_UPPER,
            getSubOptionFromList('Upper', normalized?.overbite?.improveDeepBite)
              ?.options?.value
          ),
          anteriorBiteRamps: convertToOptionsType(
            ANTERIOR_BITE_RAMPS,
            getSubOptionFromList('Upper', normalized?.overbite?.improveDeepBite)
              ?.options?.anteriorBiteRampsOptions
          ),
          upperOtherText:
            getSubOptionFromList('Upper', normalized?.overbite?.improveDeepBite)
              ?.options?.text || ''
        },
        Lower: {
          lower: convertToOptionsType(
            OVERBITE_IMPROVE_DEEP_BITE_LOWER,
            getSubOptionFromList('Lower', normalized?.overbite?.improveDeepBite)
              ?.options?.value
          ),
          lowerOtherText:
            getSubOptionFromList('Lower', normalized?.overbite?.improveDeepBite)
              ?.options?.text || ''
        }
      }
    },
    midline: {
      Midline: normalized?.midlines?.value || '',
      ImproveMidline: {
        Upper: {
          upper:
            getSubOptionFromList('Upper', normalized?.midlines?.options)
              ?.value || '',
          adjustPatient:
            getSubOptionFromList('Upper', normalized?.midlines?.options)
              ?.specification ||
            upperMidlineShift ||
            ''
        },
        Lower: {
          lower:
            getSubOptionFromList('Lower', normalized?.midlines?.options)
              ?.value || '',
          adjustPatient:
            getSubOptionFromList('Lower', normalized?.midlines?.options)
              ?.specification ||
            lowerMidlineShift ||
            ''
        }
      }
    },
    spacing: {
      Spacing: normalized?.spacing?.value?.[0] || '',
      upper:
        getSubOptionFromList('Upper', normalized?.spacing?.options)?.value ||
        '',
      lower:
        getSubOptionFromList('Lower', normalized?.spacing?.options)?.value ||
        '',
      toothSpacing: {
        space: normalized?.spacing?.type || '',
        toothChart: getToothChartValues(normalized?.spacing?.leaveSpacing)
      }
    },
    crowding: {
      Crowding: convertToOptionsType(CROWDING, normalized?.crowding?.value),
      resolveCrowding: {
        lower: {
          archExpansion: lowerResolveCrowding?.archExpansion || '',
          molarDistalization: convertToOptionsType(
            RESOLVE_CROWDING_OPTION_LOWER,
            lowerResolveCrowding?.molarDistalization
          ),
          proclination: lowerResolveCrowding?.proclination || '',
          ipr: {
            Ipr: lowerResolveCrowding?.ipr?.value || '',
            anteriorTeeth:
              lowerResolveCrowding?.ipr?.primaryIpr?.anteriorTeeth || '',
            rightPosteriorTeeth:
              lowerResolveCrowding?.ipr?.primaryIpr?.rightPosteriorTeeth || '',
            leftPosteriorTeeth:
              lowerResolveCrowding?.ipr?.primaryIpr?.leftPosteriorTeeth || ''
          }
        },
        upper: {
          archExpansion: upperResolveCrowding?.archExpansion || '',
          molarDistalization: convertToOptionsType(
            RESOLVE_CROWDING_OPTION_UPPER,
            upperResolveCrowding?.molarDistalization
          ),
          proclination: upperResolveCrowding?.proclination || '',
          ipr: {
            Ipr: upperResolveCrowding?.ipr?.value || '',
            anteriorTeeth:
              upperResolveCrowding?.ipr?.primaryIpr?.anteriorTeeth || '',
            rightPosteriorTeeth:
              upperResolveCrowding?.ipr?.primaryIpr?.rightPosteriorTeeth || '',
            leftPosteriorTeeth:
              upperResolveCrowding?.ipr?.primaryIpr?.leftPosteriorTeeth || ''
          }
        }
      }
    },
    extraction: {
      Extraction: normalized?.crowdingExtraction?.value || '',
      dentitionSelection: convertToOptionsType(
        DENTITION_TYPE_OPTIONS,
        extractionDentitionValues
      ),
      toothChart: formatTeethsForController(extractionAdultTeeth),
      primaryToothChart: formatTeethsForController(extractionPrimaryTeeth)
    },
    auxiliaries: {
      isPresent: normalized?.isAuxiliariesPresent ?? false,
      buttons: formatTeethsForController(normalized?.auxiliaryButtons),
      precisionCuts: formatTeethsForController(
        normalized?.auxiliaryPrecisionCuts
      )
    },
    additionalAppliances: {
      isRequired: normalized?.isAdditionalApplianceRequired ?? false,
      tads: normalized?.tads || '',
      sectionalBraces: normalized?.sectionalBraces || '',
      otherAppliances: normalized?.otherAppliances || ''
    },
    specialNotes: normalized?.specialNotes || '',
    translatedSpecialNotes: normalized?.translatedSpecialNotes || ''
  };
};

export const getDefaultZenClearUltraTreatmentSpec =
  (): UltraTreatmentSpecType =>
    formatUltraForForm({ ultraArchesToTreat: ARCHES.BOTH });
